/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { supabase } from "@/lib/auth/supabaseClient";
import type {
  Notification,
  NotificationSettings,
  NotificationType,
} from "@/types/notification";

// Re-export types for use in other modules
export type { NotificationSettings };

export interface CreateNotificationData {
  recipient_id: string;
  type: string;
  message: string;
  actor_id?: string | null;
  actor_name?: string | null;
  actor_avatar_url?: string | null;
  is_anonymous?: boolean;
}

export interface SendNotificationParams {
  recipient_id: string;
  type: NotificationType;
  actor_id?: string | null;
  actor_name?: string | null;
  actor_avatar_url?: string | null;
  message: string;
  is_anonymous?: boolean;
}

export const notificationsService = {
  // Get user notifications
  async getNotifications(
    userId: string,
    limit = 20,
    offset = 0
  ): Promise<{ data: Notification[]; count: number }> {
    const { data, count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact" })
      .eq("recipient_id", userId)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);
    if (error) throw error;
    return { data: (data || []) as Notification[], count: count || 0 };
  },

  // Get unread notification count
  async getUnreadCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("recipient_id", userId)
      .eq("read", false);

    if (error) throw error;
    return count || 0;
  },

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", notificationId);

    if (error) throw error;
  },

  // Mark all notifications as read
  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("recipient_id", userId)
      .eq("read", false);

    if (error) throw error;
  },

  // Create notification
  async createNotification(
    data: CreateNotificationData
  ): Promise<Notification> {
    const { data: notification, error } = await supabase
      .from("notifications")
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return notification as Notification;
  },

  // Send notification
  async sendNotification(
    params: SendNotificationParams
  ): Promise<string | null> {
    try {
      // Validate required parameters
      if (!params.recipient_id) {
        throw new Error("Recipient ID is required");
      }

      if (!params.type) {
        throw new Error("Notification type is required");
      }

      const { data, error } = await supabase.rpc("send_notification", {
        _recipient_id: params.recipient_id,
        _type: params.type,
        _actor_id: params.actor_id || null,
        _actor_name: params.actor_name || null,
        _actor_avatar_url: params.actor_avatar_url || null,
        _message: params.message,
        _is_anonymous: params.is_anonymous || false,
      });

      if (error) throw error;
      return data;
    } catch (error: unknown) {
      console.error("Error sending notification:", error);
      throw error;
    }
  },

  // Delete notification
  async deleteNotification(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);

    if (error) throw error;
  },

  // Get notification settings
  async getNotificationSettings(userId: string): Promise<NotificationSettings> {
    const { data, error } = await supabase
      .from("notification_settings")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error && error.code !== "PGRST116") throw error;

    // Return defaults if no settings found
    if (!data) {
      return {
        notify_views: true,
        notify_likes: true,
        notify_digest: true,
        email_daily: false,
        email_weekly: true,
        email_realtime: false,
      };
    }

    return data;
  },

  // Update notification settings
  async updateNotificationSettings(
    userId: string,
    settings: Partial<NotificationSettings>
  ): Promise<NotificationSettings> {
    const { data, error } = await supabase
      .from("notification_settings")
      .upsert({
        user_id: userId,
        ...settings,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Subscribe to real-time notifications
  subscribeToNotifications(
    userId: string,
    callback: (notification: Notification) => void
  ) {
    return supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new as Notification);
        }
      )
      .subscribe();
  },
};
